import { useCallback, useEffect, useRef, useState } from "react";
import {
  type TenediCrownImportReportFieldsFragment,
  useApplyTenediCrownImportMutation,
  useCreateTenediCrownImportUploadMutation,
  usePlanTenediCrownImportMutation,
} from "../../generated/graphql";
import {
  crownExportFileProblem,
  EXPORT_ZIP_CONTENT_TYPE,
} from "./crown-import";

export type CrownImportReport = TenediCrownImportReportFieldsFragment;

export type CrownImportState =
  | { readonly step: "idle" }
  | { readonly step: "uploading" | "planning" }
  | {
      readonly step: "planned" | "replanning" | "applying";
      readonly uploadKey: string;
      readonly report: CrownImportReport;
      /** The cutover choice `report` was planned with, which the apply sends. */
      readonly confirmCutover: boolean;
    }
  | { readonly step: "applied"; readonly report: CrownImportReport };

const IDLE: CrownImportState = { step: "idle" };

const messageOf = (error: unknown): string =>
  error instanceof Error ? error.message : String(error);

export type CrownImportApply = {
  readonly confirmProduction: boolean | undefined;
};

/**
 * Runs the Crown export import for one carrier: uploads the zip to the URL
 * the backend mints, plans it (a dry run), then applies that plan. A new
 * carrier, or `reset`, starts over, and a response for a run that has since
 * been replaced is dropped, so a slow plan never lands on another carrier.
 *
 * A new file is planned without cutover. Changing the cutover choice plans the
 * same upload again with it, and the apply sends the choice the shown plan was
 * made with, so the apply writes what the dry run showed.
 *
 * The backend deletes the upload once an apply runs, successful or not, so a
 * failed apply goes back to idle and the operator chooses the file again.
 * `apply` resolves to null for a run replaced or unmounted while it ran, so
 * a late import never reloads a connection picked since.
 */
const useCrownImport = (companyUuid: string | null) => {
  const [state, setState] = useState<CrownImportState>(IDLE);
  const [error, setError] = useState<string | null>(null);
  const run = useRef(0);

  const [createUpload] = useCreateTenediCrownImportUploadMutation({
    errorPolicy: "none",
  });
  const [planImport] = usePlanTenediCrownImportMutation({
    errorPolicy: "none",
  });
  const [applyImport] = useApplyTenediCrownImportMutation({
    errorPolicy: "none",
  });

  const planUpload = useCallback(
    async ({
      uploadKey,
      confirmCutover,
    }: {
      readonly uploadKey: string;
      readonly confirmCutover: boolean;
    }): Promise<CrownImportReport | null> => {
      if (companyUuid === null) {
        return null;
      }
      const planned = await planImport({
        variables: { input: { companyUuid, uploadKey, confirmCutover } },
      });
      const report = planned.data?.planTenediCrownImport;
      if (report === undefined) {
        throw new Error("The backend returned no plan.");
      }
      return report;
    },
    [companyUuid, planImport],
  );

  const reset = useCallback(() => {
    run.current += 1;
    setState(IDLE);
    setError(null);
  }, []);

  useEffect(() => {
    reset();
    return () => {
      run.current += 1;
    };
  }, [companyUuid, reset]);

  const plan = useCallback(
    async (file: File): Promise<void> => {
      if (companyUuid === null) {
        return;
      }
      run.current += 1;
      const thisRun = run.current;
      const current = () => run.current === thisRun;
      const problem = crownExportFileProblem(file);
      if (problem !== null) {
        setState(IDLE);
        setError(problem);
        return;
      }
      setError(null);
      setState({ step: "uploading" });
      try {
        const upload = await createUpload({
          variables: {
            input: { companyUuid, fileName: file.name, sizeBytes: file.size },
          },
        });
        const target = upload.data?.createTenediCrownImportUpload;
        if (target === undefined) {
          throw new Error("The backend returned no upload URL.");
        }
        const response = await fetch(target.uploadUrl, {
          method: "PUT",
          body: file,
          headers: { "Content-Type": EXPORT_ZIP_CONTENT_TYPE },
        });
        if (!response.ok) {
          throw new Error(`The upload failed (HTTP ${response.status}).`);
        }
        if (!current()) {
          return;
        }
        setState({ step: "planning" });
        const report = await planUpload({
          uploadKey: target.uploadKey,
          confirmCutover: false,
        });
        if (report !== null && current()) {
          setState({
            step: "planned",
            uploadKey: target.uploadKey,
            report,
            confirmCutover: false,
          });
        }
      } catch (error_) {
        if (current()) {
          setState(IDLE);
          setError(messageOf(error_));
        }
      }
    },
    [companyUuid, createUpload, planUpload],
  );

  /** Plans the same upload again with `confirmCutover`; a failure keeps the previous plan. */
  const setCutover = useCallback(
    async (confirmCutover: boolean): Promise<void> => {
      if (state.step !== "planned" || state.confirmCutover === confirmCutover) {
        return;
      }
      run.current += 1;
      const thisRun = run.current;
      const previous = state;
      setError(null);
      setState({ ...previous, step: "replanning", confirmCutover });
      try {
        const report = await planUpload({
          uploadKey: previous.uploadKey,
          confirmCutover,
        });
        if (report !== null && run.current === thisRun) {
          setState({ ...previous, report, confirmCutover });
        }
      } catch (error_) {
        if (run.current === thisRun) {
          setState(previous);
          setError(messageOf(error_));
        }
      }
    },
    [planUpload, state],
  );

  const apply = useCallback(
    async ({
      confirmProduction,
    }: CrownImportApply): Promise<CrownImportReport | null> => {
      if (companyUuid === null || state.step !== "planned") {
        return null;
      }
      run.current += 1;
      const thisRun = run.current;
      const planned = state;
      setError(null);
      setState({ ...planned, step: "applying" });
      try {
        const applied = await applyImport({
          variables: {
            input: {
              companyUuid,
              uploadKey: planned.uploadKey,
              confirmCutover: planned.confirmCutover,
              confirmProduction,
            },
          },
        });
        const report = applied.data?.applyTenediCrownImport;
        if (report === undefined) {
          throw new Error("The backend returned no result.");
        }
        if (run.current !== thisRun) {
          return null;
        }
        setState({ step: "applied", report });
        return report;
      } catch (error_) {
        if (run.current === thisRun) {
          setState(IDLE);
          setError(`${messageOf(error_)} Choose the export again to retry.`);
        }
        return null;
      }
    },
    [applyImport, companyUuid, state],
  );

  return { state, error, plan, setCutover, apply, reset };
};

export default useCrownImport;
